/* eslint-disable indent */
import algoliasearch from 'algoliasearch';
import { createSignal, For, onCleanup, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { SearchCviIcon } from '../icons-library/search-cvi';
import clsx from 'clsx';
import removeAmpersand from '../../tools/remove-ampersand';
import { fieldShellClasses } from '../forms-tw/forms-fields/primitives';

type SearchbarProps = {
    resultSetter?: (results: SearchResults) => void;
    enabledPostTypes?: ContentTypeSlug[];
    excludedEndpoints?: string[];
    onPostSelect: (selection: ContentItem) => void;
}

export type ContentTypeSlug = 'video-item' | 'page' | 'product' | 'blog-and-news' | 'event';

export type ContentItem = {
    isHealthcareProfessional: boolean;
    objectID: string;
    postTitle: string;
    tags: string[];
    texts: string;
    type: ContentTypeSlug;
    url: string;
}

type SearchResults = {
    [key in ContentTypeSlug]: any[];
};


export const Searchbar = (props: SearchbarProps) => {
    const { localize, algoliaSettings, siteInfo, productsEntityName, eventsEntityName, blogAndNewsEntityName, videosEntityName } = useContext(AppContext);

    const [searchText, setSearchText] = createSignal('');
    const [searchResult, setSearchResult] = createSignal<SearchResults>();
    const [isDropdownVisible, setIsDropdownVisible] = createSignal(false);
    const [hasClickedSubmitButton, setHasClickedSubmitButton] = createSignal(false);

    const nameMap: { [key in ContentTypeSlug]: string } = {
        'video-item': videosEntityName,
        'page': localize('pages', 'Pages'),
        'product': productsEntityName,
        'blog-and-news': blogAndNewsEntityName,
        'event': eventsEntityName,
    };

    const { searchAppId, searchApiClientKey } = algoliaSettings;
    if (!searchAppId || !searchApiClientKey) {
        return <div>No searchAppId / searchApiClientKey was found..</div>;
    }

    const { siteId, target } = siteInfo;
    if (!siteId || !target) {
        return <div>No siteId / target was found..</div>;
    }

    const client = algoliasearch(searchAppId, searchApiClientKey); // search only api key

    const searchClient = client.initIndex(`${siteId}-${target}`);

    const searchDropdownItems = async () => {
        try {
            const algoliaResults = await searchClient.search(searchText(), { hitsPerPage: 10 });
            const hits = algoliaResults.hits as ContentItem[];

            const sortedHits: SearchResults = {
                'video-item': [],
                'page': [],
                'product': [],
                'blog-and-news': [],
                'event': [],
            };

            hits.forEach((hit: { type: ContentTypeSlug, url: string }) => {
                if (props.enabledPostTypes && !props.enabledPostTypes.includes(hit.type)) {
                    return;
                }

                if (props?.excludedEndpoints?.includes(hit.url)) {
                    return;
                }

                sortedHits[hit.type].push({
                    ...hit,
                    url: hit.url === '' ? '/' : hit.url, // Empty string is the start page
                });
            });

            setSearchResult(sortedHits);
            if (props.resultSetter) {
                props.resultSetter(sortedHits);
            }
        } catch (error) {
            console.error('error searching', error);
        }
    };

    const handleOnSearchInputChange = (event: any) => {
        setSearchText(event.target.value);
        searchDropdownItems();

        if (searchText()) {
            setIsDropdownVisible(true);
        } else {
            setIsDropdownVisible(false);
        }
    };

    const handleOnDropdownItemClick = (item: ContentItem) => {
        setSearchText('');
        props.onPostSelect(item);
        setIsDropdownVisible(false);
        setHasClickedSubmitButton(!hasClickedSubmitButton());
    };

    let formRef: any;
    let dropdownRef: any;
    const dropdownId = 'searchbar-standalone-results';
    onMount(() => {
        const handleClick = (event: any) => {
            const isClickInsideForm = formRef.contains(event.target);
            const isClickInsideDropdown = dropdownRef.contains(event.target);

            if (isClickInsideForm) {
                searchDropdownItems();
            }

            if (!isClickInsideDropdown && !isClickInsideForm) {
                setIsDropdownVisible(false);
            }
        };
        document.addEventListener('click', handleClick);
        onCleanup(() => document.removeEventListener('click', handleClick));
    });

    const hasSearchResults = () => {
        if (!searchResult()) {
            return false;
        }

        let hasResults = false;
        for (const key in searchResult()) {
            // Checks if any of the categories has any results
            if (( searchResult() as SearchResults)[key as ContentTypeSlug].length > 0) {
                hasResults = true;
            }
        }

        return hasResults;
    };
        

    return (
        <ErrorCatcher componentName="Searchbar">
            {/* DS FormKit Search (Storybook `components-formkit-search`) with the autocomplete
                results panel from `components-formkit-autocomplete`. The panel floats over the
                content below instead of pushing it, like the DS popover. */}
            <div class="relative mt-cvi-8">
                <form class="relative" novalidate role="search" ref={formRef}>
                    <input
                        class={clsx(fieldShellClasses(), 'pr-cvi-13')}
                        type="search"
                        placeholder={localize('search', 'Search...')}
                        aria-label={localize('search', 'Search')}
                        aria-expanded={isDropdownVisible()}
                        aria-controls={dropdownId}
                        autocomplete="off"
                        onInput={handleOnSearchInputChange}
                        value={searchText()}
                    />
                    <button
                        class="absolute inset-y-0 right-0 flex cursor-pointer items-center border-0 bg-transparent px-cvi-5 text-cvi-fg-muted transition-colors duration-200 hover:text-cvi-fg-link"
                        type="submit"
                        aria-label={localize('search', 'Search')}
                    >
                        <SearchCviIcon size="medium" ariaHidden={true} />
                    </button>
                </form>

                <ul
                    id={dropdownId}
                    role="listbox"
                    class={clsx(
                        'absolute inset-x-0 top-full z-10 m-0 mt-cvi-3 list-none rounded-cvi-md border border-cvi-border-default bg-cvi-bg-elevation-overlay p-cvi-4 shadow-elevation-overlay',
                        isDropdownVisible() ? 'block' : 'hidden',
                    )}
                    ref={dropdownRef}
                >
                    <Show
                        when={hasSearchResults()}
                        fallback={
                            <li class="cursor-default px-cvi-4 py-cvi-3 body-md font-cvi-regular text-cvi-fg-muted">
                                {localize('no-results', 'No results...')}
                            </li>
                        }
                    >
                        <For each={Object.keys(searchResult() || {}) as ContentTypeSlug[]}>
                            {(categorySlug: ContentTypeSlug) => {
                                if (props.enabledPostTypes && !props.enabledPostTypes.includes(categorySlug)) {
                                    return;
                                }

                                return (
                                    <Show when={ searchResult()?.[categorySlug].length }>
                                        <li role="presentation" class="px-cvi-4 pb-cvi-2 pt-cvi-4 label-sm font-cvi-medium uppercase text-cvi-fg-muted first:pt-cvi-2">
                                            { nameMap[categorySlug] }
                                        </li>
                                        <For each={ searchResult()?.[categorySlug] }>
                                            {(item: ContentItem) => (
                                                <li
                                                    role="option"
                                                    aria-selected={false}
                                                    class="cursor-pointer rounded-cvi-sm px-cvi-4 py-cvi-3 body-md font-cvi-regular text-cvi-fg-default transition-colors duration-200 hover:bg-cvi-bg-menu-hover"
                                                    onClick={() => handleOnDropdownItemClick(item)}
                                                >
                                                    { removeAmpersand(item?.postTitle) }
                                                </li>
                                            )}
                                        </For>
                                    </Show>
                                );
                            }}
                        </For>
                    </Show>
                </ul>
            </div>
        </ErrorCatcher>
    );
};
